/* eslint-disable jsx-a11y/label-has-associated-control, react/jsx-props-no-spreading */
import React from 'react';
import {useTranslation} from 'react-i18next';

import first from 'lodash/first';
import identity from 'lodash/identity';
import noop from 'lodash/noop';

import {useEvent} from '@edna/hooks';

import {formField, newFormField} from '../FormField';
import * as S from './style';

type Files = File[];
type TValue = File | Files;

type TProps = {
  id?: string;
  className?: string;
  name?: string;
  value?: any;
  onChange: (value: TValue) => void;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isInvalid?: boolean;
  onFocus?: TEmptyFunction;
  onBlur?: TEmptyFunction;
  isFileValid?: (value: Files) => boolean;
};

type TFileProps = {
  onRemove: (file: File) => void;
  file: File;
  disabled?: boolean;
  isInvalid?: boolean;
};

const FileComponent = React.memo<TFileProps>(({onRemove, file, disabled, isInvalid}) => {
  const handleRemoveFile = React.useCallback(() => {
    onRemove(file);
  }, [file, onRemove]);

  return (
    <S.File isInvalid={isInvalid} disabled={disabled}>
      <S.FileName>{file.name}</S.FileName>
      <S.RemoveFileButton
        type="button"
        title="remove button"
        disabled={disabled}
        onClick={handleRemoveFile}
      >
        <S.RemoveFileIcon />
      </S.RemoveFileButton>
    </S.File>
  );
});

const getFiles = (value?: any, multiple?: boolean): Files => {
  if (multiple) {
    return Array.isArray(value) && value.every((file) => file instanceof File) ? value : [];
  }

  if (value instanceof File) {
    return [value];
  }

  return [];
};

const defaultIsFileValid = () => true;

const UploadArea = React.memo<TProps>(
  ({
    id: propId,
    className,
    name,
    value,
    multiple,
    accept,
    disabled,
    readOnly,
    isInvalid,
    onChange = noop,
    onFocus = noop,
    onBlur = noop,
    isFileValid = defaultIsFileValid,
  }) => {
    const {t} = useTranslation();
    const convertValue = multiple ? identity : first;
    const files = React.useMemo(() => getFiles(value, multiple), [multiple, value]);

    const id = propId ?? name;

    const innerRef = React.useRef<HTMLInputElement>(null);
    const isFocusedRef = React.useRef(false);

    const isSelectorVisible = multiple || files.length === 0;

    const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        const nextFiles: TValue = Array.from(event.target.files ?? []);

        if (!isFileValid(nextFiles) || disabled || readOnly) {
          return;
        }

        onChange(convertValue(nextFiles));
      },
      [isFileValid, disabled, readOnly, onChange, convertValue],
    );

    const handleFocus = React.useCallback(() => {
      if (!disabled) {
        onFocus();
        isFocusedRef.current = true;
      }
    }, [disabled, onFocus]);

    const handleBlur = React.useCallback(() => {
      if (!disabled) {
        onBlur();
        isFocusedRef.current = false;
      }
    }, [disabled, onBlur]);

    const handleKeyDown = React.useCallback(
      (event: KeyboardEvent) => {
        if (isFocusedRef.current && event.code === 'Space' && innerRef.current && !disabled) {
          event.preventDefault();
          innerRef.current.click();
        }
      },
      [disabled],
    );

    const handleRemoveFile = React.useCallback(
      (file) => {
        onChange(convertValue(files.filter((f) => f !== file)));
      },
      [convertValue, files, onChange],
    );

    useEvent('keydown', handleKeyDown);

    return (
      <div className={className}>
        {isSelectorVisible && (
          <S.Area
            onFocus={handleFocus}
            onBlur={handleBlur}
            isInvalid={isInvalid}
            multiple={multiple}
            disabled={disabled}
            readOnly={readOnly}
          >
            <S.UploadIcon />
            <div>
              {t(
                multiple
                  ? ['EdnaUploadArea:multiple', 'UploadArea:multiple']
                  : ['EdnaUploadArea:single', 'UploadArea:single'],
              )}
            </div>
            <S.Input
              id={id}
              type="file"
              name={name}
              onChange={handleChange}
              multiple={multiple}
              disabled={disabled || readOnly}
              accept={accept}
              tabIndex={-1}
              ref={innerRef}
            />
          </S.Area>
        )}
        {files.map((file) => (
          <FileComponent
            file={file}
            disabled={disabled}
            isInvalid={isInvalid}
            onRemove={handleRemoveFile}
            key={`${file.name}/${file.lastModified}`}
          />
        ))}
      </div>
    );
  },
);

export type {TProps};

export const UploadAreaField = formField<TProps, TValue>(UploadArea);

export const NewUploadAreaField = newFormField<TProps>(UploadArea);

export default UploadArea;

/* eslint-enable jsx-a11y/label-has-associated-control, react/jsx-props-no-spreading */
